import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "..";
import type {
  ActionReportsParams,
  RenewalsResponse,
  RenewalsActionReportParams,
  WhosNewResponse,
  WhosCloseResponse,
  WhosCloseActionReportParams,
  BirthdayActionReportParams,
  BirthdayActionReportResponse,
  RetroPvInquiryParams,
  RetroPvInquiryResponse,
  NbbvActionReportParams,
  NbbvResponse,
  CvrActionReportParams,
  CvrResponse,
  CbrActionReportParams,
  CbrResponse,
  SaqActionReportParams,
  SaqResponse,
  FsiTrackingActionReportParams,
  FsiTrackingResponse,
} from "./ActionReportAPI.types";
import type { IboOnboardingParams, IboOnboardingResponse } from "./IboOnboardingAPI.types";

export const actionReportsApi = createApi({
  reducerPath: "actionReportsApi",
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    getWhosNew: builder.query<WhosNewResponse, ActionReportsParams>({
      query: (params) => {
        const { Aff, Abo, Period, Locale } = params;
        const queryParams = new URLSearchParams({
          Aff,
          Abo,
          Period,
          Locale,
        });

        return `/acc-action-report-v2-api/api/whosnew?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getRenewals: builder.query<RenewalsResponse, RenewalsActionReportParams>({
      query: (params) => {
        const { Aff, Abo, Period, TimeZone, Locale } = params;
        const queryParams = new URLSearchParams({
          Aff,
          Abo,
          Period,
          TimeZone,
          Locale,
        });

        return `/acc-action-report-v2-api/api/renewals?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getWhosClose: builder.query<WhosCloseResponse, WhosCloseActionReportParams>({
      query: (params) => {
        const { Aff, Abo, Period, Locale, StartRange, EndRange, BusinessNature } = params;
        const queryParams = new URLSearchParams({
          Aff,
          Abo,
          Period,
          Locale,
          StartRange,
          EndRange,
          BusinessNature,
        });

        return `/acc-action-report-v2-api/api/whosclose?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getBirthdays: builder.query<BirthdayActionReportResponse, BirthdayActionReportParams>({
      query: (params) => {
        const { Aff, Abo, Period, Locale, BusinessNature } = params;
        const queryParams = new URLSearchParams({
          Aff,
          Abo,
          Period,
          Locale,
          BusinessNature,
        });

        return `/acc-action-report-v2-api/api/birthday?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getRetroPvInquiry: builder.query<RetroPvInquiryResponse, RetroPvInquiryParams>({
      query: ({ Aff, Abo, Locale }) => {
        const queryParams = new URLSearchParams({ Aff, Abo, Locale });
        return `/acc-action-report-v2-api/api/retro-pv-inquiry?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getCvr: builder.query<CvrResponse, CvrActionReportParams>({
      query: (params) => {
        const { Aff, Abo, Period, Locale } = params;
        const queryParams = new URLSearchParams({
          Aff,
          Abo,
          Period,
          Locale,
        });

        return `/acc-action-report-v2-api/api/cvr?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getIboOnboarding: builder.query<IboOnboardingResponse, IboOnboardingParams>({
      query: (params) => {
        const { Aff, Abo, Period, Locale, BlockFilter, StartEntryDate, EndEntryDate, BusinessNature } = params;
        const queryParams = new URLSearchParams({
          Aff,
          Abo,
          Period,
          Locale,
        });

        if (BlockFilter !== undefined) {
          queryParams.set("BlockFilter", BlockFilter);
        }

        if (StartEntryDate !== undefined) {
          queryParams.set("StartEntryDate", StartEntryDate);
        }

        if (EndEntryDate !== undefined) {
          queryParams.set("EndEntryDate", EndEntryDate);
        }

        if (BusinessNature !== undefined) {
          queryParams.set("BusinessNature", BusinessNature);
        }

        return `/acc-action-report-v2-api/api/ibo/onboarding?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getNbbv: builder.query<NbbvResponse, NbbvActionReportParams>({
      query: (params) => {
        const { Aff, Abo, Period, Locale } = params;
        const queryParams = new URLSearchParams({
          Aff,
          Abo,
          Period,
          Locale,
        });

        return `/acc-action-report-v2-api/api/nbbv?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getCbr: builder.query<CbrResponse, CbrActionReportParams>({
      query: (params) => {
        const { Aff, Abo, Period, Locale } = params;
        const queryParams = new URLSearchParams({
          Aff,
          Abo,
          Period,
          Locale,
        });

        return `/acc-action-report-v2-api/api/cbr?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getSaq: builder.query<SaqResponse, SaqActionReportParams>({
      query: (params) => {
        const { Aff, Abo, Period, Locale, TimeZone } = params;
        const queryParams = new URLSearchParams({
          aff: Aff,
          abo: Abo,
          period: Period,
          locale: Locale,
          timezone: TimeZone,
        });

        return `/acc-action-report-v2-api/api/saq?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
    getFsiTrackingActionReport: builder.query<FsiTrackingResponse, FsiTrackingActionReportParams>({
      query: (params) => {
        const { Aff, Abo, Period, Locale } = params;
        const queryParams = new URLSearchParams({
          Aff,
          Abo,
          Period,
          Locale,
        });

        return `/acc-action-report-v2-api/api/fsiactionreport?${queryParams}`;
      },
      extraOptions: { urlBase: "apiCFBaseUrl" },
    }),
  }),
});

export const {
  useGetWhosNewQuery,
  useGetRenewalsQuery,
  useGetWhosCloseQuery,
  useGetBirthdaysQuery,
  useGetRetroPvInquiryQuery,
  useGetCvrQuery,
  useGetIboOnboardingQuery,
  useGetNbbvQuery,
  useGetCbrQuery,
  useGetSaqQuery,
  useGetFsiTrackingActionReportQuery,
} = actionReportsApi;
